interface ValidationResult {
  isValid: boolean;
  errors: string[];
  sanitizedValue?: string;
}

export class EnhancedInputValidator {
  /**
   * Sanitize and validate text input with XSS protection
   */
  sanitizeAndValidateText(input: string, maxLength: number = 100, allowedChars?: RegExp): ValidationResult {
    const errors: string[] = [];
    
    // Remove dangerous HTML tags and scripts
    let sanitized = this.sanitizeText(input, maxLength);
    
    // Check length after sanitization
    if (sanitized.length > maxLength) {
      errors.push(`Must not exceed ${maxLength} characters`);
      sanitized = sanitized.substring(0, maxLength);
    }
    
    // Check allowed characters if specified
    if (allowedChars && !allowedChars.test(sanitized)) {
      errors.push('Contains invalid characters');
    }
    
    // Check for suspicious patterns
    if (this.containsSuspiciousPatterns(sanitized)) {
      errors.push('Contains potentially harmful content');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitized
    };
  }

  /**
   * Validate password for authentication - allows quotes but maintains other security checks
   */
  validatePasswordForAuthentication(password: string, maxLength: number = 128): ValidationResult {
    const errors: string[] = [];
    
    // Check length
    if (password.length > maxLength) {
      errors.push(`Must not exceed ${maxLength} characters`);
    }
    
    // Check for suspicious patterns but exclude quotes from the check
    if (this.containsSuspiciousPasswordPatterns(password)) {
      errors.push('Contains potentially harmful content');
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: password // Don't sanitize passwords, return as-is
    };
  }

  /**
   * Validate and sanitize name fields
   */
  validateName(name: string, fieldName: string): ValidationResult {
    const errors: string[] = [];
    const sanitizedName = name.trim();
    
    // Length validation
    if (sanitizedName.length < 1) {
      errors.push(`${fieldName} is required`);
    } else if (sanitizedName.length > 50) {
      errors.push(`${fieldName} must not exceed 50 characters`);
    }
    
    // Character whitelist: letters (including accented), spaces, hyphens, apostrophes
    const nameRegex = /^[a-zA-ZÀ-ÿ\s'-]+$/;
    if (sanitizedName && !nameRegex.test(sanitizedName)) {
      errors.push(`${fieldName} can only contain letters, spaces, hyphens, and apostrophes`);
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitizedName
    };
  }

  /**
   * Validate and sanitize email input
   */
  validateEmail(email: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedEmail = email.trim().toLowerCase();

    // Length check
    if (sanitizedEmail.length > 254) {
      errors.push('Email address is too long');
    }

    // Format validation
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    if (!emailRegex.test(sanitizedEmail)) {
      errors.push('Invalid email format');
    }

    // Security checks
    if (this.containsSQLInjection(sanitizedEmail) || this.containsXSS(sanitizedEmail)) {
      errors.push('Email contains invalid characters');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitizedEmail
    };
  }

  /**
   * Enhanced username validation
   */
  validateUsername(username: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedUsername = username.trim();

    // Length validation
    if (sanitizedUsername.length < 3) {
      errors.push('Username must be at least 3 characters');
    } else if (sanitizedUsername.length > 20) {
      errors.push('Username must not exceed 20 characters');
    }

    // Character validation - alphanumeric, underscore, hyphen only
    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(sanitizedUsername)) {
      errors.push('Username can only contain letters, numbers, underscores, and hyphens');
    }

    // Security checks
    if (this.containsSQLInjection(sanitizedUsername) || this.containsXSS(sanitizedUsername)) {
      errors.push('Username contains invalid characters');
    }

    // Reserved words check
    const reservedWords = ['admin', 'root', 'system', 'null', 'undefined', 'api', 'www', 'support', 'help'];
    if (reservedWords.includes(sanitizedUsername.toLowerCase())) {
      errors.push('Username is reserved');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitizedUsername
    };
  }

  /**
   * Enhanced password validation
   */
  validatePassword(password: string): ValidationResult {
    const errors: string[] = [];

    // Length validation
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters');
    } else if (password.length > 128) {
      errors.push('Password must not exceed 128 characters');
    }

    // Complexity requirements
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }

    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }

    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number');
    }

    if (!/[^a-zA-Z0-9]/.test(password)) {
      errors.push('Password must contain at least one special character');
    }

    // Common weak passwords
    const commonPasswords = ['password', '123456', 'qwerty', 'admin', 'letmein', '12345678'];
    if (commonPasswords.includes(password.toLowerCase())) {
      errors.push('Password is too common');
    }

    // Check for suspicious patterns (password-specific)
    if (this.containsSuspiciousPasswordPatterns(password)) {
      errors.push('Password contains potentially harmful content');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  /**
   * Validate phone number with enhanced security
   */
  validatePhone(phone: string, countryCode: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedPhone = phone.replace(/\D/g, ''); // Keep only digits

    // Length validation
    if (sanitizedPhone.length < 7) {
      errors.push('Phone number is too short');
    } else if (sanitizedPhone.length > 15) {
      errors.push('Phone number is too long');
    }

    // Security check on original input
    if (this.containsSuspiciousPatterns(phone)) {
      errors.push('Phone number contains invalid characters');
    }

    // Add country code if not present
    if (!sanitizedPhone.startsWith(countryCode.replace('+', ''))) {
      sanitizedPhone = countryCode.replace('+', '') + sanitizedPhone;
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: '+' + sanitizedPhone
    };
  }

  /**
   * Validate address field
   */
  validateAddress(address: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedAddress = this.sanitizeText(address, 200);

    if (sanitizedAddress.length > 200) {
      errors.push('Address must not exceed 200 characters');
    }

    // Allow letters, numbers, spaces, common punctuation
    const addressRegex = /^[a-zA-Z0-9\s.,#-]*$/;
    if (sanitizedAddress && !addressRegex.test(sanitizedAddress)) {
      errors.push('Address contains invalid characters');
    }

    if (this.containsSuspiciousPatterns(sanitizedAddress)) {
      errors.push('Address contains potentially harmful content');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitizedAddress
    };
  }

  /**
   * Validate city field
   */
  validateCity(city: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedCity = this.sanitizeText(city, 100);

    if (sanitizedCity.length > 100) {
      errors.push('City must not exceed 100 characters');
    }

    // Allow letters, spaces, hyphens, apostrophes
    const cityRegex = /^[a-zA-ZÀ-ÿ\s'-]*$/;
    if (sanitizedCity && !cityRegex.test(sanitizedCity)) {
      errors.push('City can only contain letters, spaces, hyphens, and apostrophes');
    }

    if (this.containsSuspiciousPatterns(sanitizedCity)) {
      errors.push('City contains potentially harmful content');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitizedCity
    };
  }

  /**
   * Validate postal code
   */
  validatePostalCode(postalCode: string): ValidationResult {
    const errors: string[] = [];
    let sanitizedPostalCode = postalCode.trim().toUpperCase();

    if (sanitizedPostalCode.length > 20) {
      errors.push('Postal code must not exceed 20 characters');
    }

    // Allow alphanumeric, spaces, hyphens
    const postalRegex = /^[a-zA-Z0-9\s-]*$/;
    if (sanitizedPostalCode && !postalRegex.test(sanitizedPostalCode)) {
      errors.push('Postal code can only contain letters, numbers, spaces, and hyphens');
    }

    if (this.containsSuspiciousPatterns(sanitizedPostalCode)) {
      errors.push('Postal code contains potentially harmful content');
    }

    return {
      isValid: errors.length === 0,
      errors,
      sanitizedValue: sanitizedPostalCode
    };
  }

  /**
   * Enhanced text sanitization
   */
  sanitizeText(input: string, maxLength = 1000): string {
    return input
      .trim()
      // Remove script tags and their content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      // Remove dangerous HTML tags
      .replace(/<(script|iframe|object|embed|form|input|button)[^>]*>/gi, '')
      // Remove event handlers
      .replace(/on\w+\s*=\s*["'][^"']*["']/gi, '')
      // Remove javascript: protocols
      .replace(/javascript\s*:/gi, '')
      // Remove data: protocols (can be used for XSS)
      .replace(/data\s*:/gi, '')
      // Remove other HTML tags but keep content
      .replace(/<[^>]*>/g, '')
      // Remove dangerous characters for SQL injection
      .replace(/[<>&"']/g, (match) => {
        const htmlEntities: { [key: string]: string } = {
          '<': '&lt;',
          '>': '&gt;',
          '&': '&amp;',
          '"': '&quot;',
          "'": '&#x27;'
        };
        return htmlEntities[match] || match;
      })
      .substring(0, maxLength);
  }

  /**
   * Check for suspicious patterns that might indicate malicious content
   */
  private containsSuspiciousPatterns(input: string): boolean {
    const suspiciousPatterns = [
      // XSS patterns
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /data:/i,
      /vbscript:/i,
      // SQL injection patterns
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/i,
      /(--|\/\*|\*\/|;)/,
      /(\bOR\b|\bAND\b).*[=<>]/i,
      // Command injection
      /(\||&|;|\$\(|\`)/,
      // Path traversal
      /\.\.\//,
      // Null bytes
      /\x00/
    ];

    return suspiciousPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Check for suspicious patterns in passwords - excludes quotes to allow legitimate passwords
   */
  private containsSuspiciousPasswordPatterns(input: string): boolean {
    const suspiciousPatterns = [
      // XSS patterns
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /data:/i,
      /vbscript:/i,
      // SQL injection patterns (exclude quotes and semicolons for passwords)
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/i,
      /(--|\/\*|\*\/)/,
      /(\bOR\b|\bAND\b).*[=<>]/i,
      // Command injection (exclude some chars that are valid in passwords)
      /(\||&|\$\(|\`)/,
      // Path traversal
      /\.\.\//,
      // Null bytes
      /\x00/
    ];

    return suspiciousPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Check for SQL injection patterns
   */
  private containsSQLInjection(input: string): boolean {
    const sqlPatterns = [
      /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|UNION|SCRIPT)\b)/i,
      /(--|\/\*|\*\/|;|'|")/,
      /(\bOR\b|\bAND\b).*[=<>]/i,
      /(\bUNION\b|\bSELECT\b).*(\bFROM\b|\bWHERE\b)/i
    ];

    return sqlPatterns.some(pattern => pattern.test(input));
  }

  /**
   * Check for XSS patterns
   */
  private containsXSS(input: string): boolean {
    const xssPatterns = [
      /<script/i,
      /javascript:/i,
      /on\w+\s*=/i,
      /<iframe/i,
      /<object/i,
      /<embed/i,
      /data:/i,
      /vbscript:/i,
      /%3cscript/i,
      /&#x3c;script/i
    ];

    return xssPatterns.some(pattern => pattern.test(input));
  }
}

export const enhancedInputValidator = new EnhancedInputValidator();
